import { Injectable, inject } from '@angular/core';
import { Device } from '@capacitor/device';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import {
  catchError,
  combineLatest,
  forkJoin,
  from,
  map,
  of,
  switchMap,
  tap,
  timeout
} from 'rxjs';
import { AuthActions } from '@components/auth/auth.actions';
import { ApiHealthTrackerBodyBatteryService } from '@providers/api/health-tracker/body-battery/body-battery.service';
import { ApiHealthTrackerSleepService } from '@providers/api/health-tracker/sleep/sleep.service';
import { ApiHealthTrackerStressService } from '@providers/api/health-tracker/stress/stress.service';
import { WearablesIntegrationService } from '@providers/api/wearables-integration/wearables-integration';
import {
  WearablesIntegrationUserWearable,
  WearablesIntegrationWearable,
  WearablesIntegrationWearablesType
} from '@providers/api/wearables-integration/wearables-integration.interface';
import { Session } from '@providers/session/session.service';
import { TrackerCoreActions, TrackerSettingsActions } from '../actions';
import { TrackerService } from '../tracker.service';
import { ActiveTracker } from '../tracker.state.interface';

/**
 * @description Tracker Core Effects.
 * @exports
 * @class TrackerCoreEffects
 */
@Injectable()
export class TrackerCoreEffects extends TrackerService {
  private readonly actions = inject(Actions);
  private readonly bodyBatteryService = inject(
    ApiHealthTrackerBodyBatteryService
  );
  private readonly session = inject(Session);
  private readonly sleepService = inject(ApiHealthTrackerSleepService);
  private readonly store = inject(Store);
  private readonly stressService = inject(ApiHealthTrackerStressService);
  private readonly wearablesIntegration = inject(WearablesIntegrationService);

  readonly fetchConnectedTrackers = createEffect(
    () => {
      return this.actions.pipe(
        ofType(TrackerCoreActions.fetchConnectedTrackers),
        switchMap(() => {
          if (this.session.isAuthenticated()) {
            return this.wearablesIntegration.getUserWearable().pipe(
              timeout(5000),
              catchError(() => {
                return of({ data: { results: [] } });
              })
            );
          }
          return of({ data: { results: [] } });
        }),
        switchMap((userWearableResponse) => {
          if (
            userWearableResponse.data?.results &&
            Array.isArray(userWearableResponse.data.results)
          ) {
            return combineLatest({
              trackers: this.wearablesIntegration
                .getWearables({
                  ids: userWearableResponse.data.results.map(
                    (item) => item.wearableAndTrackerId
                  )
                })
                .pipe(
                  map(
                    (res) =>
                      res.data?.results ??
                      ([] as Array<WearablesIntegrationWearable>)
                  ),
                  catchError(() => {
                    return of([] as Array<WearablesIntegrationWearable>);
                  })
                ),
              userTrackers: of(userWearableResponse.data.results),
              deviceInfo: from(Device.getInfo()).pipe(
                catchError(() => {
                  return of({ platform: 'unknown' });
                })
              )
            });
          }
          return combineLatest({
            trackers: of([] as Array<WearablesIntegrationWearable>),
            userTrackers: of([] as Array<WearablesIntegrationUserWearable>),
            deviceInfo: from(Device.getInfo()).pipe(
              catchError(() => {
                return of({ platform: 'unknown' });
              })
            )
          });
        }),
        map((response) => {
          return TrackerCoreActions.fetchConnectedTrackersSuccess({
            trackers: response.trackers,
            userTrackers: response.userTrackers
          });
        }),
        catchError((error: any) => {
          return of(
            TrackerCoreActions.fetchConnectedTrackersFailed({
              error: error?.message ?? 'Unknown error occurred'
            })
          );
        })
      );
    },
    {
      dispatch: true
    }
  );

  readonly fetchDailyHealthSnapshot = createEffect(() => {
    return this.actions.pipe(
      ofType(TrackerCoreActions.fetchDailyHealthSnapshot),
      switchMap(() => {
        const today = DateTime.now().toISODate(); // YYYY-MM-DD format
        return forkJoin({
          bodyBattery: this.bodyBatteryService
            .fetchDayData({ date: today, codes: ['GARMIN'] })
            .pipe(map((response) => response.data)),
          sleepScore: this.sleepService
            .fetchDayData({ date: today, codes: ['GARMIN'] })
            .pipe(map((response) => response.data)),
          stressLevel: this.stressService
            .fetchDayData({ date: today, codes: ['GARMIN'] })
            .pipe(map((response) => response.data))
        });
      }),
      map((data) => {
        return TrackerCoreActions.fetchDailyHealthSnapshotSuccess({ data });
      }),
      catchError((error: any) =>
        of(
          TrackerCoreActions.fetchDailyHealthSnapshotFailed({
            error: error?.message
          })
        )
      )
    );
  });

  readonly onAuthLogout_SwitchProfile = createEffect(
    () => {
      return this.actions.pipe(
        ofType(AuthActions.logout, AuthActions.switchProfile),
        map(() => TrackerCoreActions.reset())
      );
    },
    { dispatch: true }
  );

  readonly onConnectedTrackerChanged = createEffect(
    () => {
      return this.actions.pipe(
        ofType(
          TrackerCoreActions.connectedTrackerChanged,
          AuthActions.switchProfile
        ),
        tap(() => {
          this.store.dispatch(TrackerCoreActions.resetTrackerData());
          setTimeout(() => {
            this.store.dispatch(TrackerCoreActions.fetchConnectedTrackers());
            this.store.dispatch(
              TrackerSettingsActions.fetchUserDisclaimerAgreed()
            );
            this.store.dispatch(
              TrackerSettingsActions.fetchUserHealthCategorySettings()
            );
          }, 500);
        })
      );
    },
    {
      dispatch: false
    }
  );

  readonly onLogin = createEffect(
    () => {
      return this.actions.pipe(
        ofType(AuthActions.login),
        tap(() => {
          this.store.dispatch(TrackerCoreActions.fetchConnectedTrackers());
          this.store.dispatch(
            TrackerSettingsActions.fetchUserDisclaimerAgreed()
          );
        }),
        map(() => TrackerCoreActions.reset())
      );
    },
    { dispatch: true }
  );

  readonly setActiveTracker = createEffect(() =>
    this.actions.pipe(
      ofType(TrackerCoreActions.fetchConnectedTrackersSuccess),
      switchMap((actionPayload) =>
        combineLatest({
          deviceInfo: from(Device.getInfo()).pipe(
            catchError(() => {
              return of({ platform: 'unknown' });
            })
          ),
          actionPayload: of(actionPayload)
        })
      ),
      map((response) => {
        const cTracker: Array<ActiveTracker> = [];
        for (const item of response.actionPayload.userTrackers) {
          const tracker = response.actionPayload.trackers.find(
            (tracker) => tracker.id === item.wearableAndTrackerId
          );
          if (tracker) {
            cTracker.push({
              ...item,
              tracker
            });
          }
        }
        if (cTracker.length > 0) {
          const fitnessApp = cTracker.find((item): boolean => {
            if (
              item.tracker.type === WearablesIntegrationWearablesType.TRACKER
            ) {
              return true;
            }
            return false;
          });
          const wearable = cTracker.find(
            (item) =>
              item.tracker.type === WearablesIntegrationWearablesType.WEARABLE
          );
          return TrackerCoreActions.setActiveTracker({
            activeTracker: wearable ?? fitnessApp ?? undefined
          });
        }
        return TrackerCoreActions.setActiveTracker({
          activeTracker: undefined
        });
      })
    )
  );
}
